// Décor au sol stable (props posés sur les tiles) — varie selon le biome élémentaire.
// Génère une liste stable de props par (roomId, themeId) qui peuvent être dessinés
// SOUS le joueur via la fonction drawTileDecor.
//
// Position d'un prop : { wx, wy, type } en coordonnées tile (room-local).
// Évite la zone centrale (interactable) et les bords (murs/portes).

const rand = (seed) => {
  let a = seed | 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const stringSeed = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return h >>> 0;
};

// Pool de types de props par biome
const TILE_PROPS = {
  forest: ["mushroom", "grass", "flower", "bush"],
  cavern: ["crystal", "pebbles", "stalagmite", "puddle"],
  ruins: ["rubble", "broken_column", "pebbles", "moss_patch"],
  glacial: ["ice_shard", "snow_pile", "frosted_rock"],
  volcano: ["lava_crack", "ember_pile", "char_rock", "skull_small"],
  celestial: ["star_glyph", "rune_circle", "feather"],
  astral: ["void_rift", "star_glyph", "purple_glyph"],
  abyss: ["bone_pile", "purple_smoke_vent", "shadow_rune", "skull_small"],
};

const PROP_COUNT_RANGE = [4, 8]; // 4–8 props par salle (denses pour rendre vivant)

// Cache stable par (themeId, roomId)
const cache = new Map();

const buildProps = (themeId, roomId, ROOM_W, ROOM_H, excludeCenter = true) => {
  const key = `${themeId}::${roomId}::${ROOM_W}x${ROOM_H}`;
  if (cache.has(key)) return cache.get(key);

  const r = rand(stringSeed(key));
  const pool = TILE_PROPS[themeId] || TILE_PROPS.forest;
  const min = PROP_COUNT_RANGE[0];
  const max = PROP_COUNT_RANGE[1];
  const count = min + Math.floor(r() * (max - min + 1));

  const out = [];
  let attempts = 0;
  while (out.length < count && attempts < 200) {
    attempts++;
    const wx = 1.0 + r() * (ROOM_W - 2.0);
    const wy = 1.0 + r() * (ROOM_H - 2.0);
    // Exclude near center (interactable zone): 2.2 tile radius
    if (excludeCenter) {
      const dx = wx - ROOM_W / 2;
      const dy = wy - ROOM_H / 2;
      if (dx * dx + dy * dy < 4.84) continue;
    }
    // Avoid overlapping with another prop
    const tooClose = out.some((p) => Math.hypot(p.wx - wx, p.wy - wy) < 0.9);
    if (tooClose) continue;
    const type = pool[Math.floor(r() * pool.length)];
    out.push({ wx, wy, type, sway: r() });
  }
  cache.set(key, out);
  return out;
};

// Iso projection (must match IsometricMap's): center-anchored, tile 64x32
const isoProject = (wx, wy, ROOM_W, ROOM_H, canvasW, canvasH) => {
  const cx = ROOM_W / 2;
  const cy = ROOM_H / 2;
  const TILE_W = 64;
  const TILE_H = 32;
  return {
    x: (wx - cx - (wy - cy)) * (TILE_W / 2) + canvasW / 2,
    y: (wx - cx + (wy - cy)) * (TILE_H / 2) + canvasH / 2,
  };
};

export const drawTileDecor = (ctx, ROOM_W, ROOM_H, canvasW, canvasH, now, themeId, roomId) => {
  const props = buildProps(themeId, roomId || "default", ROOM_W, ROOM_H);
  for (const p of props) {
    const s = isoProject(p.wx, p.wy, ROOM_W, ROOM_H, canvasW, canvasH);
    drawProp(ctx, s.x, s.y, p.type, now, p.sway);
  }
};

function drawProp(ctx, x, y, type, now, swayBase) {
  const sway = Math.sin(now / 700 + swayBase * 10) * 1.4;
  switch (type) {
    case "mushroom": {
      // Foot shadow
      ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.beginPath();
      ctx.ellipse(x, y + 2, 8, 3, 0, 0, Math.PI * 2); ctx.fill();
      // Stem
      ctx.fillStyle = "#fef3c7";
      ctx.fillRect(x - 2, y - 10, 4, 10);
      // Cap (red)
      ctx.fillStyle = "#dc2626";
      ctx.beginPath(); ctx.arc(x, y - 12, 6, Math.PI, 0); ctx.fill();
      // White spots
      ctx.fillStyle = "#fef3c7";
      ctx.beginPath(); ctx.arc(x - 2, y - 14, 1.2, 0, Math.PI * 2);
      ctx.arc(x + 2, y - 13, 1, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "grass": {
      ctx.strokeStyle = "#16a34a"; ctx.lineWidth = 1.5;
      for (let i = -3; i <= 3; i += 2) {
        ctx.beginPath();
        ctx.moveTo(x + i, y);
        ctx.lineTo(x + i + sway * 0.5, y - 8);
        ctx.stroke();
      }
      break;
    }
    case "flower": {
      ctx.fillStyle = "#16a34a"; ctx.fillRect(x - 0.5, y - 8, 1, 8);
      const colors = ["#ec4899", "#a78bfa", "#fde047", "#fb7185"];
      const col = colors[Math.floor(swayBase * 4) % colors.length];
      ctx.fillStyle = col;
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * 2.5, y - 9 + Math.sin(a) * 2.5, 1.6, 0, Math.PI * 2); ctx.fill();
      }
      ctx.fillStyle = "#fde047"; ctx.beginPath(); ctx.arc(x, y - 9, 1.4, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "bush": {
      ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.beginPath();
      ctx.ellipse(x, y + 2, 10, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#166534";
      ctx.beginPath(); ctx.arc(x - 4, y - 4, 5, 0, Math.PI * 2);
      ctx.arc(x + 4, y - 4, 5, 0, Math.PI * 2);
      ctx.arc(x, y - 8, 6, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#22c55e";
      ctx.beginPath(); ctx.arc(x - 2, y - 7, 2, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "crystal": {
      const flicker = 0.6 + 0.4 * Math.sin(now / 400 + swayBase * 10);
      ctx.fillStyle = `rgba(167, 139, 250, ${flicker})`;
      ctx.beginPath();
      ctx.moveTo(x, y - 14); ctx.lineTo(x + 5, y - 5); ctx.lineTo(x, y); ctx.lineTo(x - 5, y - 5); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#1e1b4b"; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = `rgba(196, 181, 253, ${flicker})`;
      ctx.beginPath();
      ctx.moveTo(x, y - 14); ctx.lineTo(x + 2, y - 7); ctx.lineTo(x, y - 5); ctx.closePath(); ctx.fill();
      break;
    }
    case "pebbles": {
      ctx.fillStyle = "#475569";
      ctx.beginPath(); ctx.ellipse(x - 4, y, 3, 2, 0, 0, Math.PI * 2);
      ctx.ellipse(x + 3, y - 1, 2.5, 1.8, 0, 0, Math.PI * 2);
      ctx.ellipse(x, y + 1, 2, 1.4, 0, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "stalagmite": {
      ctx.fillStyle = "#334155";
      ctx.beginPath(); ctx.moveTo(x - 5, y); ctx.lineTo(x, y - 16); ctx.lineTo(x + 5, y); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = "rgba(165, 180, 252, 0.5)";
      ctx.beginPath(); ctx.moveTo(x - 1, y - 14); ctx.lineTo(x + 1, y - 16); ctx.lineTo(x, y - 10); ctx.closePath(); ctx.fill();
      break;
    }
    case "puddle": {
      const sh = 0.4 + 0.2 * Math.sin(now / 600 + swayBase * 5);
      ctx.fillStyle = `rgba(56, 189, 248, ${sh})`;
      ctx.beginPath(); ctx.ellipse(x, y, 10, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.5)"; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.ellipse(x, y - 0.5, 6, 1.5, 0, 0, Math.PI * 2); ctx.stroke();
      break;
    }
    case "rubble": {
      ctx.fillStyle = "#78716c";
      ctx.fillRect(x - 5, y - 3, 4, 3);
      ctx.fillRect(x + 1, y - 4, 3, 4);
      ctx.fillRect(x - 2, y - 1, 3, 2);
      ctx.strokeStyle = "#1c1917"; ctx.strokeRect(x - 5, y - 3, 4, 3);
      break;
    }
    case "broken_column": {
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.beginPath(); ctx.ellipse(x, y + 2, 7, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#d6d3d1"; ctx.fillRect(x - 5, y - 14, 10, 14);
      ctx.fillStyle = "#a8a29e"; ctx.fillRect(x - 6, y - 16, 12, 3);
      ctx.fillStyle = "#1c1917"; ctx.fillRect(x - 3, y - 12, 1, 9);
      break;
    }
    case "moss_patch": {
      ctx.fillStyle = "rgba(22, 163, 74, 0.45)";
      ctx.beginPath(); ctx.ellipse(x, y, 8, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "rgba(101, 163, 13, 0.5)";
      ctx.beginPath(); ctx.arc(x - 3, y - 1, 2, 0, Math.PI * 2);
      ctx.arc(x + 3, y, 2, 0, Math.PI * 2);
      ctx.arc(x, y - 1, 1.5, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "ice_shard": {
      const flicker = 0.5 + 0.5 * Math.sin(now / 500 + swayBase * 10);
      ctx.fillStyle = `rgba(186, 230, 253, ${flicker})`;
      ctx.beginPath();
      ctx.moveTo(x, y - 12); ctx.lineTo(x + 4, y); ctx.lineTo(x - 4, y); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#0ea5e9"; ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.beginPath(); ctx.moveTo(x, y - 12); ctx.lineTo(x - 1, y - 5); ctx.lineTo(x + 1, y - 5); ctx.closePath(); ctx.fill();
      break;
    }
    case "snow_pile": {
      ctx.fillStyle = "rgba(0,0,0,0.2)";
      ctx.beginPath(); ctx.ellipse(x, y + 2, 9, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#f0f9ff";
      ctx.beginPath(); ctx.arc(x, y - 2, 7, Math.PI, 0); ctx.fill();
      ctx.fillStyle = "#bae6fd";
      ctx.beginPath(); ctx.arc(x, y - 2, 7, Math.PI, 0); ctx.stroke();
      break;
    }
    case "frosted_rock": {
      ctx.fillStyle = "#475569";
      ctx.beginPath(); ctx.ellipse(x, y - 2, 6, 5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#e0e7ff";
      ctx.beginPath(); ctx.arc(x, y - 5, 4, Math.PI, 0); ctx.fill();
      break;
    }
    case "lava_crack": {
      const flicker = 0.5 + 0.5 * Math.sin(now / 200 + swayBase * 10);
      ctx.strokeStyle = `rgba(251, 146, 60, ${flicker})`; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 6, y); ctx.lineTo(x - 2, y - 1); ctx.lineTo(x + 1, y + 1); ctx.lineTo(x + 6, y - 1); ctx.stroke();
      ctx.strokeStyle = `rgba(254, 240, 138, ${flicker})`; ctx.lineWidth = 0.8; ctx.stroke();
      break;
    }
    case "ember_pile": {
      ctx.fillStyle = "#1c1917";
      ctx.beginPath(); ctx.ellipse(x, y, 6, 2.5, 0, 0, Math.PI * 2); ctx.fill();
      for (let i = 0; i < 4; i++) {
        const flicker = 0.4 + 0.6 * Math.sin(now / 150 + swayBase * 10 + i);
        ctx.fillStyle = `rgba(251, 146, 60, ${flicker})`;
        ctx.beginPath(); ctx.arc(x - 4 + i * 2.5, y - 1, 1.4, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case "char_rock": {
      ctx.fillStyle = "#1c1917";
      ctx.beginPath(); ctx.ellipse(x, y - 1, 5, 4, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#7c2d12"; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(x - 3, y - 2); ctx.lineTo(x + 2, y - 4); ctx.stroke();
      break;
    }
    case "skull_small": {
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.beginPath(); ctx.ellipse(x, y + 2, 5, 2, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#e7e5e4";
      ctx.beginPath(); ctx.arc(x, y - 3, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillRect(x - 2, y, 4, 2);
      ctx.fillStyle = "#1c1917";
      ctx.fillRect(x - 2.5, y - 3, 1.5, 1.6);
      ctx.fillRect(x + 1, y - 3, 1.5, 1.6);
      break;
    }
    case "star_glyph": {
      const pulse = 0.5 + 0.5 * Math.sin(now / 400 + swayBase * 10);
      ctx.fillStyle = `rgba(254, 240, 138, ${0.6 * pulse})`;
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const r1 = 5, r2 = 2;
        if (i === 0) ctx.moveTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
        else ctx.lineTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
        const a2 = a + Math.PI / 5;
        ctx.lineTo(x + Math.cos(a2) * r2, y + Math.sin(a2) * r2);
      }
      ctx.closePath(); ctx.fill();
      break;
    }
    case "rune_circle": {
      const pulse = 0.5 + 0.5 * Math.sin(now / 600 + swayBase * 8);
      ctx.strokeStyle = `rgba(254, 240, 138, ${0.45 * pulse})`; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = `rgba(252, 211, 77, ${0.4 * pulse})`;
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.stroke();
      break;
    }
    case "feather": {
      const t = now / 800 + swayBase * 10;
      ctx.save();
      ctx.translate(x, y - 8);
      ctx.rotate(Math.sin(t) * 0.3);
      ctx.fillStyle = "#fef3c7";
      ctx.beginPath();
      ctx.ellipse(0, 0, 2, 6, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#d6d3d1"; ctx.stroke();
      ctx.restore();
      break;
    }
    case "void_rift": {
      const pulse = 0.5 + 0.5 * Math.sin(now / 500 + swayBase * 6);
      ctx.fillStyle = `rgba(91, 33, 182, ${0.6 * pulse})`;
      ctx.beginPath(); ctx.ellipse(x, y, 8, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(15, 23, 42, ${0.7 * pulse})`;
      ctx.beginPath(); ctx.ellipse(x, y, 5, 2, 0, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "purple_glyph": {
      const pulse = 0.4 + 0.6 * Math.sin(now / 500 + swayBase * 10);
      ctx.strokeStyle = `rgba(216, 180, 254, ${0.6 * pulse})`; ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 5, y); ctx.lineTo(x, y - 7); ctx.lineTo(x + 5, y); ctx.lineTo(x, y + 5); ctx.closePath();
      ctx.stroke();
      break;
    }
    case "bone_pile": {
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.beginPath(); ctx.ellipse(x, y + 1, 6, 2, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#e7e5e4";
      ctx.fillRect(x - 5, y - 1, 10, 2);
      ctx.fillRect(x - 2, y - 4, 2, 6);
      ctx.beginPath(); ctx.arc(x - 5, y - 1, 1.5, 0, Math.PI * 2);
      ctx.arc(x + 5, y - 1, 1.5, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "purple_smoke_vent": {
      const t = now / 600 + swayBase * 8;
      ctx.fillStyle = "#1c1917";
      ctx.beginPath(); ctx.ellipse(x, y, 5, 2, 0, 0, Math.PI * 2); ctx.fill();
      for (let i = 0; i < 3; i++) {
        const yOff = -4 - i * 5;
        const xOff = Math.sin(t + i) * 3;
        const alpha = 0.4 - i * 0.1;
        ctx.fillStyle = `rgba(168, 85, 247, ${alpha})`;
        ctx.beginPath(); ctx.arc(x + xOff, y + yOff, 3 + i * 0.8, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case "shadow_rune": {
      const pulse = 0.4 + 0.6 * Math.sin(now / 400 + swayBase * 8);
      ctx.strokeStyle = `rgba(168, 85, 247, ${0.6 * pulse})`; ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 5, y - 3); ctx.lineTo(x + 5, y - 3);
      ctx.moveTo(x, y - 5); ctx.lineTo(x, y + 3);
      ctx.moveTo(x - 4, y + 1); ctx.lineTo(x + 4, y + 1);
      ctx.stroke();
      break;
    }
    default:
      break;
  }
}
